import { useState, useEffect, useRef, useCallback } from 'react';
import { Document as AppDocument, Note, SermonMetadata } from '../types';
import { get, post, put, del } from '../lib/api';
import { APP_EVENTS } from '../lib/brand';

interface UseDocumentResult {
  document: AppDocument | null;
  notes: Note[];
  sermonMetadata: SermonMetadata | null;
  isLoading: boolean;
  error: string | null;
  fetchDocument: () => Promise<void>;
  updateDocument: (data: Partial<AppDocument>) => Promise<void>;
  addNote: (content: string) => Promise<void>;
  deleteNote: (noteId: number) => Promise<void>;
  updateSermonMetadata: (metadata: SermonMetadata) => Promise<void>;
}

export function useDocument(documentId?: string): UseDocumentResult {
  const [document, setDocument] = useState<AppDocument | null>(null);
  /**
   * The current document, readable without going through a closure.
   *
   * TipTap registers `onUpdate` once at editor creation, so the whole save
   * chain — scheduleContentSave -> persistDocument -> updateDocument — is
   * captured at first render and never re-created. Anything read from that
   * closure is frozen at its initial value, which meant every save after the
   * first sent `expected_version: 1` and conflicted with its own previous
   * write. The version has to be read at request time, not closure time.
   */
  const docRef = useRef<AppDocument | null>(null);
  /**
   * Serialises writes to this document.
   *
   * Six different things save — debounced content, debounced title, flush on
   * blur, the retry timer, ⌘S, and the end of a programmatic write — and none
   * of them exclude each other. Two overlapping saves both read the version
   * before either returns, so the second was rejected as a conflict with our
   * own first write. Threading them through one chain means each reads the
   * version the previous one just produced.
   */
  const saveQueueRef = useRef<Promise<unknown>>(Promise.resolve());
  /** Content of our last successful write, to identify self-conflicts. */
  const lastWrittenRef = useRef<string | null>(null);
  const [notes, setNotes] = useState<Note[]>([]);
  const [sermonMetadata, setSermonMetadata] = useState<SermonMetadata | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    docRef.current = document;
  }, [document]);

  const fetchDocument = async () => {
    if (!documentId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await get<AppDocument>(`/documents/${documentId}`);
      setDocument(data);
      
      // Fetch document type to check if it's a sermon
      try {
        const docTypes = await get<{ id: number; name: string }[]>("/document-types");
        const docType = docTypes.find((type) => type.id === data.doc_type_id);
        if (docType?.name === "Sermon") {
          fetchSermonMetadata(data.id);
        }
      } catch {
        // Document type is non-essential; the document itself already loaded.
      }
      
      // Fetch document notes
      fetchNotes(data.id);
    } catch (error) {
      setError("Error loading document");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchNotes = async (docId: number) => {
    try {
      const data = await get<Note[]>(`/documents/${docId}/notes`);
      setNotes(data);
    } catch (error) {
      console.error("Error fetching notes:", error);
    }
  };

  const fetchSermonMetadata = async (docId: number) => {
    try {
      const data = await get<SermonMetadata>(`/sermons/${docId}`);
      setSermonMetadata(data);
    } catch (error) {
      console.error("Error fetching sermon metadata:", error);
    }
  };

  /**
   * Thrown when the server rejected a save because the document moved on.
   * Carries the server's copy so the caller can offer a real choice instead of
   * silently discarding one side.
   */
  const writeDocument = async (data: Partial<AppDocument>, allowSelfHeal = true) => {
    const current = docRef.current ?? document;
    if (!current) return;

    try {
      const saved = await put<AppDocument>(
        `/documents/${current.id}`,
        {
          ...data,
          expected_version: docRef.current?.version ?? current.version,
        },
        { keepalive: true },
      );
      // Update the ref synchronously; the next queued write reads it before
      // React has re-rendered.
      docRef.current = { ...(docRef.current ?? current), ...data, ...saved };
      if (typeof data.content === "string") lastWrittenRef.current = data.content;
      setDocument((prev) => ({ ...(prev ?? current), ...data, ...saved }));
    } catch (error) {
      const err = error as { status?: number; body?: { current?: AppDocument } };
      if (err?.status === 409) {
        const server = err.body?.current;

        // Distinguish a genuine conflict from one with our own earlier write.
        // If the server's copy is exactly what we last wrote, nobody else
        // touched it — we simply raced ourselves, and adopting the new version
        // and retrying once loses nothing.
        const selfConflict =
          allowSelfHeal &&
          server != null &&
          lastWrittenRef.current !== null &&
          server.content === lastWrittenRef.current;

        if (selfConflict && server) {
          docRef.current = { ...(docRef.current ?? current), version: server.version };
          return writeDocument(data, false);
        }

        const conflict = new Error(
          "This document was changed somewhere else — reload to see the latest.",
        ) as Error & { status?: number; conflict?: boolean };
        conflict.status = 409;
        conflict.conflict = true;
        throw conflict;
      }
      setError("Error updating document");
      console.error(error);
      throw error;
    }
  };

  const updateDocument = useCallback(async (data: Partial<AppDocument>) => {
    // Chain onto the previous write whether it succeeded or not, so one
    // failure doesn't wedge the queue.
    const run = () => writeDocument(data);
    const queued = saveQueueRef.current.then(run, run);
    saveQueueRef.current = queued.catch(() => undefined);
    return queued;
  }, []);

  const addNote = async (content: string) => {
    if (!document) return;

    try {
      const payload = await post<{ note?: Note } & Note>(
        `/documents/${document.id}/notes`,
        { content },
      );
      const newNote = payload?.note ?? payload;
      setNotes([newNote, ...notes]);
    } catch (error) {
      setError("Error adding note");
      console.error(error);
    }
  };

  const deleteNote = async (noteId: number) => {
    try {
      await del(`/notes/${noteId}`);
      setNotes(notes.filter(note => note.id !== noteId));
    } catch (error) {
      setError("Error deleting note");
      console.error(error);
    }
  };

  const updateSermonMetadata = async (metadata: SermonMetadata) => {
    if (!document) return;

    try {
      // PUT upserts by document id (POST /api/sermons/:id is not a route)
      const updatedMetadata = await put<SermonMetadata>(`/sermons/${document.id}`, {
        series_name: metadata.series_name || null,
        delivery_date: metadata.delivery_date || null,
        audience: metadata.audience || null,
        main_point: metadata.main_point || null,
        notes: metadata.notes || null,
        scriptures: Array.isArray(metadata.scriptures) ? metadata.scriptures : null,
        tags: Array.isArray(metadata.tags) ? metadata.tags : null,
      });
      setSermonMetadata(updatedMetadata);
    } catch (error) {
      setError("Error updating sermon metadata");
      console.error(error);
      throw error;
    }
  };

  useEffect(() => {
    if (documentId) {
      fetchDocument();
    }
  }, [documentId]);

  useEffect(() => {
    const onSynced = (event: Event) => {
      const payload = (event as CustomEvent<{ document?: AppDocument }>).detail?.document;
      if (!payload || payload.id !== docRef.current?.id) return;
      // Adopt the version the outbox just confirmed. Content is already what
      // we wrote; taking the server's version is what stops the next save
      // looking like a conflict with ourselves.
      docRef.current = { ...(docRef.current ?? payload), version: payload.version };
      setDocument((prev) => (prev ? { ...prev, version: payload.version } : prev));
    };
    window.addEventListener(APP_EVENTS.documentSynced, onSynced);
    return () => window.removeEventListener(APP_EVENTS.documentSynced, onSynced);
  }, []);

  return {
    document,
    notes,
    sermonMetadata,
    isLoading,
    error,
    fetchDocument,
    updateDocument,
    addNote,
    deleteNote,
    updateSermonMetadata,
  };
}
